import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {NGXLogger} from 'ngx-logger';
import {TermwebConfiguration} from '@twpub/core/models';
import {TermwebStatusService} from '@twpub/core/services';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {MatError, MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {map, Observable, of} from 'rxjs';

import {MatButton, MatIconButton} from '@angular/material/button';

@Component({
  selector: 'pub-termweb-connection-settings-form',
  templateUrl: './termweb-connection-settings-form.component.html',
  styleUrls: ['./termweb-connection-settings-form.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatButton, MatTooltip, MatIconButton, MatIcon, MatSuffix]
})
export class TermwebConnectionSettingsFormComponent implements OnChanges {
  @Input() configuration: TermwebConfiguration | undefined;
  @Input() showButtons = true;
  @Input() lockUrlClient = false;

  @Output() saveConfiguration: EventEmitter<TermwebConfiguration> = new EventEmitter<TermwebConfiguration>();

  configForm: FormGroup = this.fb.group({
    id: new FormControl<number>(1),
    url: new FormControl<string>('', [Validators.pattern('^(https?)://[^\\s/$.?#].[^\\s]*$'), Validators.required]),
    client: new FormControl<string>('', [Validators.required]),
    userName: new FormControl<string>('', [Validators.required]),
    password: new FormControl<string>('', [Validators.required])
  });

  passwordLabel: string = 'Password';
  hidePassword = true;

  readonly lockedTooltip = $localize`:@@termweb.connection.fieldLocked:Cannot edit when dictionaries are published`;
  readonly showPasswordTooltip = $localize`:@@termweb.connection.showPassword:Show password`;
  readonly hidePasswordTooltip = $localize`:@@termweb.connection.hidePassword:Hide password`;

  constructor(private logger: NGXLogger, private fb: FormBuilder, private statusService: TermwebStatusService, private snackbar: MatSnackBar) {
  }

  ngOnChanges(): void {
    if (this.configuration) {
      const {id, url, client, userName} = this.configuration;
      const editConfiguration = {id, url, client, userName, password: ''};
      this.passwordLabel = 'Set new password';
      this.configForm.controls.password.setValidators(null);
      this.configForm.controls.password.updateValueAndValidity()
      this.configForm.setValue(editConfiguration);
    }
    if (this.lockUrlClient) {
      this.configForm.controls.url.disable();
      this.configForm.controls.client.disable();
    } else {
      this.configForm.controls.url.enable();
      this.configForm.controls.client.enable();
    }
  }

  save() {
    this.saveAndReport().subscribe();
  }

  /** Attempts connection and emits saveConfiguration if successful. Returns Observable<boolean> indicating success. */
  saveAndReport(): Observable<boolean> {
    if (!this.configForm.valid) {
      return of(false);
    }
    const payload = this.configForm.getRawValue();
    return this.statusService.attemptConnection(payload).pipe(
      map(status => {
        if (status.ok) {
          this.saveConfiguration.emit(payload);
          this.configForm.reset();
          this.showMessage(`Connected to ${status.message}`, 'success');
          return true;
        } else {
          this.showMessage(status.message, 'error');
          return false;
        }
      })
    );
  }

  cancel() {
    this.configForm.reset();
    this.saveConfiguration.emit();
  }

  private showMessage(msg: string, type: string = 'info'): void {
    const config: MatSnackBarConfig = {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      duration: 5000,
      panelClass: ['snackbar-' + type]
    };
    this.snackbar.open(msg, undefined, config)
  }
}
